import { useStore } from "@nanostores/react";
import { openPage } from "@nanostores/router";
import { ReactElement, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as z from "zod";

import { copyGraph, createGraph, GraphSummary, updateGraph } from "../api";
import { zGraphSummary } from "../api/zod.gen.ts";
import { useServerErrors } from "../hooks/useServerErrors.ts";
import {
  $graphsMetasList,
  $modifiableGraphMeta,
  $modifiableGraphMetaContent,
} from "../stores/graph/metadata.ts";
import {
  $recentlyOpenedGraphs,
  MAX_RECENT_GRAPHS,
} from "../stores/graph/persistence.ts";
import { $router } from "../stores/router.ts";
import { Button } from "./catalyst/Button";
import { Dialog } from "./catalyst/Dialog";
import { DialogActions } from "./catalyst/DialogActions";
import { DialogBody } from "./catalyst/DialogBody";
import { DialogTitle } from "./catalyst/DialogTitle";
import { ErrorMessage } from "./catalyst/ErrorMessage.tsx";
import InputFieldGroup from "./inputs/InputFieldGroup.tsx";

type GraphValues = z.infer<typeof zGraphSummary>;

const updateRecentGraphs = (
  currentGraphs: GraphSummary[],
  newGraph: GraphSummary,
): GraphSummary[] => {
  return [
    newGraph,
    ...currentGraphs.filter((graph) => graph.id !== newGraph.id),
  ].slice(0, MAX_RECENT_GRAPHS);
};

const LoadingSkeleton = (): ReactElement => (
  <div className="animate-pulse space-y-4">
    <div className="mb-2 h-4 w-12 rounded bg-zinc-800" />
    <div className="h-10 w-full rounded bg-zinc-800" />
  </div>
);

type Props = {
  id?: string;
};

export const GraphUpsertDialog = ({ id }: Props): ReactElement => {
  const [isUpsertLoading, setIsUpsertLoading] = useState(false);
  const form = useForm<Pick<GraphValues, "name">>({
    defaultValues: {
      name: "",
    },
  });
  const { clearServerErrors, handleServerError } = useServerErrors({
    formMethods: form,
  });

  const {
    data: graphData,
    error: graphError,
    loading: graphLoading,
  } = useStore($modifiableGraphMetaContent);

  const modifiableGraph = useStore($modifiableGraphMeta);
  const isEdit = !graphLoading && Boolean(graphData);
  const isCopy = modifiableGraph?.type === "copy";

  useEffect(() => {
    if (graphData) {
      form.reset({
        name: graphData.name,
      });
    } else {
      form.reset();
    }
  }, [graphData, form, isEdit]);

  const handleSubmit = async (
    data: Pick<GraphValues, "name">,
  ): Promise<void> => {
    if (graphLoading) {
      return;
    }

    setIsUpsertLoading(true);
    clearServerErrors();

    try {
      let result;

      if (isEdit) {
        result = await updateGraph({
          body: {
            name: data.name,
          },
          path: {
            id: String(id),
          },
        });
      } else if (isCopy && modifiableGraph?.sourceGraphId) {
        result = await copyGraph({
          body: {
            name: data.name,
            nodeIds: modifiableGraph.nodeIds,
          },
          path: {
            id: modifiableGraph.sourceGraphId,
          },
        });
      } else {
        result = await createGraph({
          body: {
            name: data.name,
          },
        });
      }

      $modifiableGraphMeta.set(null);
      $graphsMetasList.invalidate();
      $modifiableGraphMetaContent.set({
        data: undefined,
        error: null,
        loading: false,
      });

      const actionText = isEdit ? "updated" : isCopy ? "copied" : "created";
      toast.success(`Workspace ${actionText} successfully`);

      if (!isEdit || isCopy) {
        const { data } = result;

        if (data?.id) {
          // Add the newly created graph to recently opened graphs
          const recentlyOpenedGraphs = $recentlyOpenedGraphs.get();
          const newGraph = {
            ...data,
          };

          $recentlyOpenedGraphs.set(
            updateRecentGraphs(recentlyOpenedGraphs, newGraph),
          );

          openPage($router, "graph", { id: data.id });
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
      handleServerError(error);

      const errorAction = isEdit ? "update" : isCopy ? "copy" : "create";
      toast.error(`Failed to ${errorAction} graph`);
    }

    setIsUpsertLoading(false);
  };

  return (
    <Dialog onClose={() => $modifiableGraphMeta.set(null)} open size="xl">
      {!graphLoading && !graphError && (
        <DialogTitle>
          {isEdit ? "Update" : isCopy ? "Copy" : "Create"} Workspace
        </DialogTitle>
      )}

      <DialogBody>
        {graphError ? (
          <ErrorMessage>{graphError.message}</ErrorMessage>
        ) : graphLoading ? (
          <LoadingSkeleton />
        ) : (
          <form
            id="graph-upsert-form"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <InputFieldGroup<Pick<GraphValues, "name">>
              errors={form.formState.errors}
              fieldName="name"
              label="Name"
              placeHolder="Enter workspace name"
              register={form.register}
              rules={{ required: "Name is required" }}
            />
          </form>
        )}
      </DialogBody>

      <DialogActions>
        {graphError && (
          <Button onClick={() => $modifiableGraphMeta.set(null)}>Cancel</Button>
        )}

        {!graphError && !graphLoading && (
          <>
            <Button onClick={() => $modifiableGraphMeta.set(null)} plain>
              Cancel
            </Button>

            <Button
              color="emerald"
              disabled={graphLoading || isUpsertLoading}
              form="graph-upsert-form"
              type="submit"
            >
              {isEdit
                ? isUpsertLoading
                  ? "Updating..."
                  : "Update"
                : isCopy
                  ? isUpsertLoading
                    ? "Copying..."
                    : "Copy"
                  : isUpsertLoading
                    ? "Creating..."
                    : "Create"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
